// Beyond AI — Knowledge Base. Grounded Q&A + curated entries (CRUD for staff). Phase 7.
import { useCallback, useEffect, useState } from "react";
import { BookOpen, Search, Sparkles, Loader2, Plus, X, Save, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Markdown from "@/components/Markdown";

const EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

export default function KnowledgeBasePage() {
    const { user } = useAuth();
    const canEdit = user && EDIT_ROLES.has(user.role);
    const [meta, setMeta] = useState({ kb_categories: [] });
    const [entries, setEntries] = useState([]);
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState(null);
    const [asking, setAsking] = useState(false);
    const [dialog, setDialog] = useState(null); // {mode, entry}
    const [denied, setDenied] = useState(false);

    const loadEntries = useCallback(async () => {
        try {
            const params = {};
            if (category) params.category = category;
            if (q) params.q = q;
            const { data } = await api.get("/ai/kb", { params });
            setEntries(data);
            setDenied(false);
        } catch (e) { if (e.response?.status === 403) setDenied(true); }
    }, [category, q]);

    useEffect(() => { api.get("/ai/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
    useEffect(() => { loadEntries(); }, [loadEntries]);

    async function ask() {
        const text = question.trim();
        if (!text) return;
        setAsking(true);
        setAnswer(null);
        try {
            const payload = { question: text };
            if (category) payload.category = category;
            const { data } = await api.post("/ai/kb/ask", payload);
            setAnswer(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not answer"); }
        finally { setAsking(false); }
    }

    async function removeEntry(id, e) {
        e.stopPropagation();
        if (!window.confirm("Delete this knowledge base entry?")) return;
        try {
            await api.delete(`/ai/kb/${id}`);
            loadEntries();
            toast.success("Entry deleted");
        } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    }

    if (denied) {
        return (
            <div data-testid="ai-kb-denied" className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to the knowledge base.</p>
            </div>
        );
    }

    return (
        <div data-testid="ai-kb-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid #1F6FE5", color: "#6F6A64" }}>Beyond AI</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F] flex items-center gap-2"><BookOpen className="w-5 h-5" style={{ color: "#1F6FE5" }} /> Knowledge Base</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>{entries.length} entries · ask a question for a grounded, cited answer.</p>
                </div>
                {canEdit && (
                    <button data-testid="ai-kb-new-button" onClick={() => setDialog({ mode: "create", entry: null })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white bg-ai-grad"><Plus className="w-3.5 h-3.5" /> New entry</button>
                )}
            </div>

            {/* Ask box */}
            <div className="bg-white border rounded-[14px] p-4" style={{ borderColor: "#E1E8F2" }}>
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 shrink-0" style={{ color: "#1F6FE5" }} />
                    <input data-testid="ai-kb-question" value={question} onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
                        placeholder="Ask the knowledge base… e.g. What checks are needed on fire doors?"
                        className="flex-1 px-3 py-2 rounded-[9px] border text-[12.7px] outline-none focus:border-[#1F6FE5] focus:shadow-[0_0_0_3px_#E3EEFD]" style={{ borderColor: "#E1E8F2" }} />
                    <button data-testid="ai-kb-ask" onClick={ask} disabled={asking || !question.trim()}
                        className="px-4 py-2 rounded-[9px] text-white bg-ai-grad text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                        {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask"}
                    </button>
                </div>
                {answer && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: "#EEF3FB" }} data-testid="ai-kb-answer">
                        <Markdown text={answer.answer} />
                        {answer.sources?.length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <span className="text-[10.5px] font-semibold" style={{ color: "#9A968F" }}>Sources:</span>
                                {answer.sources.map((s, idx) => (
                                    <span key={s.id} data-testid={`ai-kb-source-${idx}`} className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-[#EEF3FB] text-[#1F6FE5] font-medium">[{idx + 1}] {s.title}</span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-white border rounded-[8px] px-3 py-1.5 flex-1 min-w-[200px] max-w-[300px]" style={{ borderColor: "#E1E8F2" }}>
                    <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entries…" className="flex-1 bg-transparent outline-none text-[12.5px]" data-testid="ai-kb-search" />
                </div>
                <select data-testid="ai-kb-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-3 rounded-lg border bg-white text-sm" style={{ borderColor: "#E1E8F2" }}>
                    <option value="">All categories</option>
                    {meta.kb_categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            {/* Entries */}
            <div className="grid md:grid-cols-2 gap-3" data-testid="ai-kb-list">
                {entries.length === 0 && <div className="text-[12.5px] italic col-span-2 text-center py-8" style={{ color: "#9A968F" }}>No entries found.</div>}
                {entries.map((e) => (
                    <div key={e.id} data-testid={`ai-kb-entry-${e.id}`} onClick={() => canEdit && setDialog({ mode: "edit", entry: e })}
                        className={`group bg-white border rounded-[12px] p-4 ${canEdit ? "cursor-pointer hover:shadow-brand-sm" : ""}`} style={{ borderColor: "#E1E8F2" }}>
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <FileText className="w-4 h-4 shrink-0" style={{ color: "#1F6FE5" }} />
                                <span className="text-[13px] font-bold text-[#1F1F1F] truncate">{e.title}</span>
                            </div>
                            {canEdit && <button data-testid={`ai-kb-delete-${e.id}`} onClick={(ev) => removeEntry(e.id, ev)} className="opacity-0 group-hover:opacity-100 text-[#B33A2E] shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>}
                        </div>
                        <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full bg-[#EEF3FB] text-[#1F6FE5] font-medium">{e.category}</span>
                        <p className="text-[11.5px] mt-2 line-clamp-3 leading-relaxed" style={{ color: "#6F6A64" }}>{e.content}</p>
                        {e.source && <p className="text-[10px] mt-2 italic truncate" style={{ color: "#9A968F" }}>{e.source}</p>}
                    </div>
                ))}
            </div>

            {dialog && (
                <KbDialog meta={meta} mode={dialog.mode} entry={dialog.entry}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); loadEntries(); }} />
            )}
        </div>
    );
}

function KbDialog({ meta, mode, entry, onClose, onSaved }) {
    const [form, setForm] = useState({ title: "", category: meta.kb_categories?.[0] || "General guidance", content: "", tags: "", source: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (entry) setForm({ title: entry.title, category: entry.category, content: entry.content, tags: (entry.tags || []).join(", "), source: entry.source || "" });
    }, [entry]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
        setBusy(true);
        const payload = { title: form.title, category: form.category, content: form.content, source: form.source || null, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
        try {
            if (mode === "create") { await api.post("/ai/kb", payload); toast.success("Entry created"); }
            else { await api.patch(`/ai/kb/${entry.id}`, payload); toast.success("Entry updated"); }
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="ai-kb-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[620px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E1E8F2" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-center justify-between" style={{ borderColor: "#EEF3FB" }}>
                    <h2 className="font-display text-[18px] font-bold text-[#1F1F1F]">{mode === "create" ? "New entry" : "Edit entry"}</h2>
                    <button data-testid="ai-kb-dialog-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F8FB] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-3">
                    <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Title *</span>
                        <input data-testid="ai-kb-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="ai-kb-fi" /></label>
                    <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Category</span>
                        <select data-testid="ai-kb-form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="ai-kb-fi">
                            {meta.kb_categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select></label>
                    <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Content *</span>
                        <textarea data-testid="ai-kb-form-content" rows={7} value={form.content} onChange={(e) => set("content", e.target.value)} className="ai-kb-fi" /></label>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Tags (comma-separated)</span>
                            <input data-testid="ai-kb-form-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} className="ai-kb-fi" /></label>
                        <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Source</span>
                            <input data-testid="ai-kb-form-source" value={form.source} onChange={(e) => set("source", e.target.value)} className="ai-kb-fi" /></label>
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: "#EEF3FB" }}>
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E1E8F2" }}>Cancel</button>
                    <button data-testid="ai-kb-form-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white bg-ai-grad">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save"}
                    </button>
                </div>
                <style>{`.ai-kb-fi{width:100%;padding:8px 11px;background:#fff;border:1px solid #E1E8F2;border-radius:8px;font-size:12.5px;color:#1F1F1F;outline:none;}.ai-kb-fi:focus{border-color:#1F6FE5;box-shadow:0 0 0 3px #E3EEFD;}`}</style>
            </div>
        </div>
    );
}
